import type { ConnectRefusalCode } from "@vidimo/shared/contracts";
import { linkedSupportChatPath } from "@/lib/chats-path";
import type { PendingLiveActivityAction } from "@/lib/native/live-activity";
import { appRoutes } from "@/lib/routes";

/**
 * Allowlist + parser for inbound `vidimo://` deep links.
 *
 * The custom URL scheme is registered on iOS (`CFBundleURLTypes`) so the Live
 * Activity's iOS 16.x button fallback can hand an action back to the app. But a
 * registered scheme is public: any other app, a webpage `<a href>`, or a QR
 * code can fire a `vidimo://…` URL at us, and the OS delivers it through the
 * same `appUrlOpen` listener with no proof of origin. So every inbound URL is
 * untrusted input — this module validates its *shape* against the small set of
 * routes the app actually owns and rejects everything else.
 *
 * Shape validation isn't enough on its own for an action that mutates state:
 * the caller additionally checks that a task-action link resolves to a task the
 * current user can act on (see `applyLiveActivityDeepLink`). This module is the
 * first gate; that lookup is the second.
 */

/** The one custom URL scheme registered for the app (iOS `CFBundleURLTypes`,
 *  Android intent filter). `URL.protocol` includes the trailing colon. */
const APP_SCHEME = "vidimo:";

/** A Live Activity lock-screen action an inbound deep link may request. */
export type LiveActivityAction = PendingLiveActivityAction["action"];

/** The actions the activity fallback is allowed to deep-link. Anything else in
 *  `?la=` is treated as no action (navigate-only), never executed. */
const LIVE_ACTIVITY_ACTIONS: readonly LiveActivityAction[] = [
  "pause",
  "resume",
  "advance",
  "finish",
];

/** Auth return leg — `vidimo://auth/callback` (host `auth`, path `/callback`). */
const AUTH_CALLBACK_HOST = "auth";
const AUTH_CALLBACK_PATH = "/callback";

/** Connect return leg (host `connect`, path `/return`). */
const CONNECT_RETURN_HOST = "connect";
const CONNECT_RETURN_PATH = "/return";

/**
 * What a channel connect page opens when the host taps "Return to Vidimo" in
 * the in-app browser, which cannot close itself. The app comes forward and
 * the browser goes (closed on iOS; cleared by the activity launch on
 * Android). Anyone can fire it: the most it does is close that browser and,
 * with an `about` from the refusal allowlist, open the Vidimo team chat on one
 * of its canned drafts, which the host still has to send.
 */
export const CONNECT_RETURN_URL = `vidimo://${CONNECT_RETURN_HOST}${CONNECT_RETURN_PATH}`;

/**
 * What a connect page opens for "Contact support" in the in-app browser: the
 * return link, so the browser goes as for a return (an app bundle older than
 * the chat hand-back still does that much), plus a refusal code from an
 * allowlist, never text, on which the app opens the Vidimo team chat.
 */
export function connectSupportUrl(about: ConnectRefusalCode): string {
  return `${CONNECT_RETURN_URL}?${new URLSearchParams({ about })}`;
}

/** Task Live Activity action link — canonical form `vidimo:///tasks/:id/live`
 *  (empty authority, absolute path). The empty host is load-bearing: a spoofed
 *  authority such as `vidimo://evil.com/tasks/:id/live` parses to the *same*
 *  pathname, so matching pathname alone would accept it. We require the host to
 *  be empty, which only the app's own emitted links are. */
const TASK_LIVE_PATH_RE = /^\/tasks\/(?<taskId>[^/]+)\/live$/u;

/** A recognized, well-formed deep-link intent. `navigatePath` is the in-app
 *  route to push — always derived here, never the raw inbound path/query, so no
 *  attacker-controlled string reaches the router. */
type DeepLink =
  | { kind: "auth-callback"; navigatePath: string }
  | { kind: "connect-return"; navigatePath?: string }
  | {
      kind: "task-live";
      taskId: string;
      action: LiveActivityAction | null;
      navigatePath: string;
    };

/**
 * Parse an inbound `appUrlOpen` URL against the route allowlist. Returns the
 * recognized intent, or `null` for anything unknown, malformed, or with a
 * spoofed authority — a `null` result means "ignore this URL". Pure and total:
 * it never throws and never has a side effect.
 */
export function parseDeepLink(rawUrl: string): DeepLink | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  if (url.protocol !== APP_SCHEME) {
    return null;
  }

  // vidimo://auth/callback — auth return leg. Navigation only; the actual
  // session exchange happens in `better-auth-capacitor` (an ASWebAuthentication
  // session that captures its own redirect), never off this URL. Query params
  // are dropped: nothing consumes them and forwarding attacker input is exactly
  // what this gate exists to stop.
  if (url.host === AUTH_CALLBACK_HOST && url.pathname === AUTH_CALLBACK_PATH) {
    return { kind: "auth-callback", navigatePath: AUTH_CALLBACK_PATH };
  }

  // vidimo://connect/return — a connect page handing the host back, to
  // whichever screen opened the browser, or with `about=<reason>` to the
  // Vidimo team chat. An unknown reason is a plain return.
  if (
    url.host === CONNECT_RETURN_HOST &&
    url.pathname === CONNECT_RETURN_PATH
  ) {
    const navigatePath = linkedSupportChatPath(url.searchParams.get("about"));
    return navigatePath
      ? { kind: "connect-return", navigatePath }
      : { kind: "connect-return" };
  }

  // vidimo:///tasks/:id/live?la=<action> — Live Activity action (empty host).
  if (url.host === "") {
    const groups = url.pathname.match(TASK_LIVE_PATH_RE)?.groups;
    if (groups) {
      const { taskId } = groups;
      const requested = new URLSearchParams(url.search).get("la");
      // Only an allowlisted action is carried through; an unknown/absent `la`
      // becomes a view-only open (navigate, no mutation).
      const action = LIVE_ACTIVITY_ACTIONS.find((a) => a === requested) ?? null;
      return {
        action,
        kind: "task-live",
        navigatePath: appRoutes.app.taskLive(taskId),
        taskId,
      };
    }
  }

  return null;
}

/**
 * Close the in-app browser for a connect return, on iOS only. On Android the
 * link reaches the singleTask main activity, which clears the browser above
 * it on the way in; `Browser.close` after that starts a fresh, invisible
 * browser controller over the app. On iOS it rejects when no browser is open,
 * as for a return link fired from anywhere but a connect page.
 */
export function closeBrowserForConnectReturn(
  platform: string,
  close: () => Promise<void>
): void {
  if (platform !== "ios") {
    return;
  }
  close().catch(() => undefined);
}

/** What the dispatcher needs from the app shell, injected so the dispatch
 *  decision stays pure and unit-testable (no React render, no Capacitor). */
interface DeepLinkHandlers {
  /** Apply a task Live Activity action; returns whether the task was recognized
   *  — i.e. one the current user can act on. See `applyLiveActivityDeepLink`. */
  applyTaskAction: (
    taskId: string,
    action: LiveActivityAction | null
  ) => boolean;
  /** Close the in-app browser where the platform needs it, for a connect
   *  return. */
  closeBrowser: () => void;
  /** Push an in-app route (react-router `history.push`). */
  navigate: (path: string) => void;
}

/**
 * Dispatch one inbound `appUrlOpen` URL. The `vidimo://` scheme is public —
 * any other app, a webpage, or a QR code can fire a URL at us — so every link
 * is untrusted: allowlist it via `parseDeepLink`, gate a task action on the
 * entity check, and navigate only for a recognized route. Anything unknown,
 * malformed, or unsolicited is ignored.
 *
 * Fails closed and never throws: it's wired straight into a Capacitor event
 * listener, and `applyTaskAction` reaches real collection writes that can
 * throw, so a hostile or malformed link must never crash the listener.
 */
export function handleDeepLinkOpen(
  url: string,
  handlers: DeepLinkHandlers
): void {
  try {
    const link = parseDeepLink(url);
    if (!link) {
      return;
    }
    if (link.kind === "connect-return") {
      handlers.closeBrowser();
      if (link.navigatePath) {
        handlers.navigate(link.navigatePath);
      }
      return;
    }
    if (
      link.kind === "task-live" &&
      !handlers.applyTaskAction(link.taskId, link.action)
    ) {
      // Task action naming a task this user can't act on — fail closed.
      return;
    }
    handlers.navigate(link.navigatePath);
  } catch {
    // Never let a malformed or hostile deep link crash the listener.
  }
}
